import React, {createRef, useState, useContext, useEffect} from 'react';
import { DragDropContext, Droppable, Draggable } from '@react-forked/dnd';
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import { defaultComponents, MaterialTableBody, MaterialTableBodyRow } from '../../../controls/Material-Table-Components';
import {Toolbar, useTheme} from "@material-ui/core";
import __ from "lodash";

import SystemContext from "../../../SystemContext";

import AirportLookup from "../../Airports/components/AirportsLookup";
import {ColumnWidth, ValidatorColumn} from "../../../blocks/ColumnHelper";
import CostingOtherSectorsValidator from "../validators/CostingOtherSectorsValidator";
import { MaskedNumber, ComboSelect, MaskedText } from '../../../controls';
import { useQueryClient } from 'react-query';
import { fetch as fetchCostingFuelRate } from '../../../services/costings/useCostingFuelRate';
import { formatChars } from '../../../blocks/FormatCharacters';


const SectionHeading = (props) => {
  const { children, ...other } = props;
  return (
    <div style={{marginTop: 10, marginBottom: 2, textAlign: "left", fontSize: "14px", fontWeight: "bold", ...other}}>
      {children}
    </div>
  );
}

const CostingOtherSectorsEditor = (props) => {
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const tableRef = createRef({state: {lastEditingRow: false}});
  const {isAuthenticated} = useAuth0();
  const queryClient = useQueryClient();
  const eventBus = SystemData.eventBus;
  const {record, readOnly} = props;
  const oldProps = __.cloneDeep(props);
  const {costingDate} = record;

  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null,
    defaultAddTransportType: 'TRUCK'
  });

  useEffect(()=> {
    const addTruck = async () => {
      setState({...state, defaultAddTransportType: 'TRUCK'});
      if (tableRef  && tableRef.current) {
        tableRef.current.dataManager.changeRowEditing();
        tableRef.current.setState({
          ...tableRef.current.dataManager.getRenderState(),
          showAddRow: !tableRef.current.state.showAddRow,
        });
      }
    }
    const addOnward = async () => {
      setState({...state, defaultAddTransportType: 'ONWARD'});
      if (tableRef  && tableRef.current) {
        tableRef.current.dataManager.changeRowEditing();
        tableRef.current.setState({
          ...tableRef.current.dataManager.getRenderState(),
          showAddRow: !tableRef.current.state.showAddRow,
        });
      }
    }
    if (eventBus) {
      eventBus.bind('CostingOtherSectorsEditor.addTruck', addTruck);    
      eventBus.bind('CostingOtherSectorsEditor.addOnward', addOnward);    
      return () => {
        eventBus.unbind("CostingOtherSectorsEditor.addTruck", addTruck);
        eventBus.unbind("CostingOtherSectorsEditor.addOnward", addOnward);
      }
    }

  }, [eventBus, state, tableRef]);

  const {classes, defaultValues} = props;


  const primarySectors = __.filter(record.legs, {transportType: 'PRIMARY'});
  const sectors = __.filter(record.legs, (leg)=> leg.transportType !== 'PRIMARY');
  const payloadHeaderStyle = { padding: "2px 2px", backgroundColor: "rgb(214, 214, 214)" };

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "2px 2px" },
      editable: state.canEdit?"always":"never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    const validatorColumn = ValidatorColumn(CostingOtherSectorsValidator, false);

    return [
      {...validatorColumn, resizable: false},
      {
        ...baseColumn,
        ...ColumnWidth(60,[5,2],undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Origin",
        field: "originAirportCode",
        initialEditValue: "",
        editComponent: (props) => {
          let timeout = null;
          if (!props?.rowData?.originFuelRecord && !parseInt(props?.rowData?.fuelPrice) && (props?.rowData?.originAirportCode||'').length === 3) {
            const originAirportCode = props?.rowData?.originAirportCode;
            fetchCostingFuelRate({queryClient, extraKeys:[originAirportCode, costingDate], useCache: true}).then(
              (response) => {
                let originFuelRecord = {success: false, error: response.error};
                let fuelPrice = null;
                if (response && response.success && response.rateRecord) {
                  const {rate} = response.rateRecord;
                  fuelPrice = rate;
                  originFuelRecord = {...response.rateRecord, success: true};
                }
                if (timeout) clearTimeout(timeout);
                timeout = setTimeout(() => {
                  props.onRowDataChange({...props.rowData, fuelPrice, originFuelRecord, customFuel: false});                  
                }, 10);
              // props.onRowDataChange({...props.rowData, fuelPrice, originFuelRecord});
            })
          }
          return (
            <AirportLookup
              value={props.value}
              returnType="object"
              onChange={(returnObject)=>{
                if (returnObject.found) {
                  const originAirportCode = returnObject.data.airportCode;
                  fetchCostingFuelRate({queryClient, extraKeys:[originAirportCode, costingDate], useCache: true}).then(
                    (response) => {
                      let originFuelRecord = {success: false, error: response.error};
                      let fuelPrice = null;
                      if (response && response.success && response.rateRecord) {
                        const {rate} = response.rateRecord;
                        fuelPrice = rate;
                        originFuelRecord = {...response.rateRecord, success: true};
                      }
                    props.onRowDataChange({...props.rowData, originAirportCode, fuelPrice, originFuelRecord});
                  })                  
                } else {
                  props.onRowDataChange({...props.rowData, originAirportCode: returnObject.value, fuelPrice: null, originFuelRecord:{success: false, error: "Invalid Origin Airport"}});
                }
              }}
            />
          )
        },
      },
      {
        ...baseColumn,
        ...ColumnWidth(60,2,undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Dest",
        field: "destinationAirportCode",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            returnType="object"
            onChange={(returnObject)=>{
              if (returnObject.found) {
                const data = {
                  ...props.rowData,
                  destinationAirportCode: returnObject.data.airportCode,
                  alternateAirportCode: returnObject.data.alternateAirportCode, 
                  alternateAirportIcaoCode: returnObject.data.alternateAirportIcaoCode
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(110,2,undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Transport Type",
        field: "transportType",
        initialEditValue: state.defaultAddTransportType,
        editable: false,
        render: (rowData) => (
          <ComboSelect
            id="transportType"
            group="TRANSPORT TYPES"
            subGroup=""
            fullWidth
            value={`${rowData.transportType || state.defaultAddTransportType}`}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(1,2,undefined,{width: 6, minWidth: 6, maxWidth: 6},baseColumn.headerStyle,undefined,"NONE"),
        title: " ",
        field: "",
        render: ()=>(<></>),
        editable: "never",
      },
      {
        ...baseColumn,
        ...ColumnWidth(95,2,undefined,undefined,payloadHeaderStyle,undefined,false),
        title: "Payload",
        field: "payloadUp",
        type: "numeric",
        editable: false,
        render: (rowData) => (
          <MaskedNumber 
            displayType="text"
            defaultValue={rowData.payloadUp||0}
            value={rowData.payloadUp||0}
            decimalScale={0}
            fixedDecimalScale={true}
            valueCap={props.weightUom==="TON"?150:150000}
            suffix={props.weightUom==="TON"?" tons":" kg"}
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(100,[20,2],undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Cost",
        field: "totalCost",
        type: "numeric",
        initialEditValue: '0',
        render: (rowData) => {
          return (
            <MaskedNumber 
              displayType="text"
              defaultValue={rowData.totalCost||0}
              value={rowData.totalCost||0}
              decimalScale={2}
              fixedDecimalScale={true}
              valueCap={999999.99}
              prefix="$"
              inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            />
        )},
        editComponent: (props) => { 
          return (
            <MaskedNumber 
              displayType="input"
              defaultValue={props.value||0}
              value={props.value}
              decimalScale={2}
              fixedDecimalScale={true}
              valueCap={999999.99}
              prefix="$"
              inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
              onChange={props.onChange}
            />
          )
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(700,[10,2]),
        title: "Notes",
        field: "notes",
        initialEditValue: "",
        render: rowData => (
          <div style={{textOverflow: 'ellipsis', width: 700, whiteSpace: 'nowrap', overflow: 'hidden'}}>{rowData.notes}</div>
        ),
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(200)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
    ];
  };
  
  const onDragEnd = (dragProps) => {
    if (!dragProps) return;
    if (!dragProps.source || !dragProps.destination) return;
    if (dragProps.source.index === dragProps.source.destination) return;

    if (Array.isArray(sectors)) {
      let records = [...sectors];
      let sourceRecord = records.splice(dragProps.source.index,1);
      const part1 = [...records].slice(0,dragProps.destination.index);
      const part2 = [...records].slice(dragProps.destination.index);
      const newRecords = [...part1, ...sourceRecord, ...part2];
      sectors.splice(0, sectors.length, ...newRecords);
      props.onChange({record: {...props.record, legs: [...primarySectors, ...sectors]}, oldRecord: props.record});
    }
  }

  const bodyRef = createRef();

  return (isAuthenticated &&
        <div className={classes.costingSectorsEditor}>
          <SectionHeading>Other Legs</SectionHeading>
          <MaterialTable
            tableRef={tableRef}
            data={sectors}
            style={{
              backgroundColor: "#fdfdfd",
              boxShadow: "none",
              border: "2px solid #aaaaaa",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              search: false,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.childTableHeader.backgroundColor,
                color: theme.palette.childTableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              detailPanelType: "single",
              showTitle: false,
              toolbar: false,
              padding: 'dense',
              paging: false,
              filtering: false,
              draggable: false,
            }}
            components={{
              ...defaultComponents,
              Body: (props) => (
                <DragDropContext onDragEnd={onDragEnd}>
                <Droppable
                  droppableId={"singleColumnDroppableAreaThusStaticInput"}
                >
                  {(provided) => (
                    <MaterialTableBody
                      {...props}
                      ref={bodyRef} 
                      forwardedRef={provided.innerRef} 
                      placeHolder={provided.placeholder}
                      rowHeight={40}
                      overscanRowCount={10}
                      addVirtualScrolling={true}
//                      bodyHeight={300}
                      extraEditHeight={46}
                      headerHeight={46}
                    />
                  )}
                </Droppable>
              </DragDropContext>
              ),
              Row: (props) => (
                <Draggable
                  draggableId={props.data.tableData.id.toString()}
                  index={props.data.tableData.id}
                >
                  {(provided) => {
                    return (
                      <MaterialTableBodyRow
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}  
                        {...props}
                        forwardedRef={provided.innerRef}
                      />
                    );
                  }}
                </Draggable>
              ),
            }}
            title="Costing Sectors"
            columns={getColumns({...props, canEdit: state.canEdit})}
            editable={{
              isEditable: () => {
                return state.canEdit && !readOnly;
              },
              isDeletable: () => {
                return state.canEdit && !readOnly;
              },
              onRowAdd: state.canEdit && !readOnly
                ? (newData) => new Promise((resolve, reject) => {
                  if (Array.isArray(sectors)) {
                    sectors.push({...newData, transportType: state.defaultAddTransportType});
                    props.onChange({legs: [...primarySectors, ...sectors], resolve, reject});
                  } else {
                    reject();
                  }
                })
                : undefined,
              onRowUpdate: state.canEdit && !readOnly
              ?(newData, oldData) =>
                new Promise((resolve, reject) => {
                  const sectorIdx = __.findIndex(sectors, (trip) => {
                    const tripData = __.omit(trip, ["tableData"]);
                    const oldRecord = __.omit(oldData, ["tableData"]);
                    return __.isEqual(tripData, oldRecord);
                  }); //sectors.indexOf(oldData);  
                  if (sectorIdx >= 0) {
                    sectors[sectorIdx] = newData;
                    props.onChange({legs: [...primarySectors, ...sectors], resolve, reject});
                  } else {
                    reject();
                  }
                })
                :null,
              onRowDelete: state.canEdit && !readOnly
              ?(oldData) =>
                new Promise((resolve, reject) => {
                  const sectorIdx = __.findIndex(sectors, (trip) => {
                    const tripData = __.omit(trip, ["tableData"]);
                    const oldRecord = __.omit(oldData, ["tableData"]);
                    return __.isEqual(tripData, oldRecord);
                  }); //sectors.indexOf(oldData);
                  if (sectorIdx >= 0) {
                    sectors.splice(sectorIdx, 1);
                    props.onChange({legs: [...primarySectors, ...sectors], resolve, reject});
                  } else {
                    reject();
                  }
                }): null,
            }}
        />
      </div>
  );
}

export default CostingOtherSectorsEditor;


